/**
 * The voice dock, rendered in its own window.
 *
 * Loaded when the renderer is opened with ?dock=1. It holds no session and no
 * socket - the app window pushes call state at it and it sends button presses
 * back, so there is exactly one client talking to the server no matter how
 * many windows are on screen.
 *
 * This is the only dock the desktop app shows. Being its own window is what
 * lets it sit over a full-screen game or move to a second monitor, which a
 * panel inside Pugo can never do.
 *
 * Nothing in here wraps or truncates: the window is sized to the contents, and
 * the contents never fight for the space. No drop shadow either - the window
 * is transparent, so a shadow is a grey smear on whatever is behind it rather
 * than depth.
 *
 * Everything but the drag handle is a button, and the handle is marked as the
 * window's drag region so the whole thing moves with it.
 */

import { useEffect, useState } from 'react';
import { bridge } from '@/lib/bridge';
import { cn } from '@/lib/cn';
import { DockButton, Hangup, Headset, HeadsetOff, Mic, MicOff, PingBars, ScreenIcon, Sliders } from './dockUi';
import { Icon } from '@/icons/Icon';

export type DockState = {
  name: string;
  avatar: string | null;
  role: string;
  roleLabel: string;
  muted: boolean;
  deafened: boolean;
  sharing: boolean;
  ping: number | null;
};

const EMPTY: DockState = {
  name: '', avatar: null, role: 'user', roleLabel: 'Member',
  muted: false, deafened: false, sharing: false, ping: null
};

export function DetachedDock() {
  const [state, setState] = useState<DockState>(EMPTY);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const api = bridge();
    const off = api?.dock?.onState?.((next: DockState) => setState(next));
    /*
     * Ask for whatever was pushed before this window finished loading.
     *
     * The app sends the call state the moment the call starts, which is
     * before the dock exists to hear it - so without this the dock sat with
     * an empty picture and "Member" until some other change happened to
     * trigger a second push.
     */
    void api?.dock?.ready?.();
    return off;
  }, []);

  // The main process owns the collapsed size, so it tells us rather than the
  // other way round - the window and the page can never disagree.
  useEffect(() => {
    const api = bridge();
    return api?.dock?.onCollapsed?.((on: boolean) => setCollapsed(on));
  }, []);

  const act = (action: string) => { void bridge()?.dock?.act?.(action); };
  const collapse = (on: boolean) => { void bridge()?.dock?.collapse?.(on); };

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-transparent p-1.5">
      <div className="flex h-full w-full select-none items-center gap-1 rounded-[calc(16px*var(--radius-mul,1))] border border-line-soft bg-[color-mix(in_srgb,var(--bg-1)_97%,var(--voice))] px-2">
        {/* the grab handle - the only part that moves the window */}
        <span
          className="drag grid flex-none cursor-grab grid-cols-2 gap-[3.5px] px-1.5 py-2 opacity-45 transition-opacity hover:opacity-90 [&>span]:bg-current"
          title="Drag to move — anywhere on your screen"
        >
          {Array.from({ length: 6 }, (_, i) => (
            <i key={i} className="h-[3px] w-[3px] rounded-full bg-mute" />
          ))}
        </span>

        {collapsed ? (
          // Collapsed: enough to prove you are still connected, and the way back.
          <button
            type="button"
            title="Expand"
            onClick={() => collapse(false)}
            className="no-drag flex flex-1 items-center justify-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] py-1.5 transition-colors hover:bg-surface-2"
          >
            <PingBars ping={state.ping} />
            <Icon name="chevronL" size={15} className="rotate-180 text-mute" />
          </button>
        ) : (
          <>
            {/* who you are and where */}
            <button
              type="button"
              onClick={() => act('open')}
              title="Back to Pugo"
              className="no-drag flex min-w-0 flex-1 items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] px-1.5 py-1 text-left transition-colors hover:bg-surface-2"
            >
              <span className="relative flex-none">
                {state.avatar ? (
                  <img src={state.avatar} alt="" className="h-[34px] w-[34px] rounded-full object-cover" />
                ) : (
                  <span className="block h-[34px] w-[34px] rounded-full bg-surface-3" />
                )}
                <span className="absolute -right-0.5 -bottom-0.5 h-[10px] w-[10px] rounded-full border-2 border-[var(--bg-1)] bg-good" />
              </span>

              <span className="min-w-0">
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <span className="text-[13.5px] font-bold">{state.name}</span>
                  {state.role !== 'user' ? (
                    <span className="rounded bg-[color-mix(in_srgb,var(--bad)_18%,transparent)] px-1 text-[9px] font-extrabold tracking-[.06em] text-bad">
                      {state.role === 'admin' ? 'ADMIN' : 'MOD'}
                    </span>
                  ) : null}
                </span>
                <span className="flex items-center gap-2 whitespace-nowrap text-[11.5px]">
                  <span className="text-mute">{state.roleLabel}</span>
                  <span className="h-3 w-px bg-line" />
                  <span className="flex items-center gap-1.5 text-voice">
                    <PingBars ping={state.ping} />
                    in voice
                  </span>
                </span>
              </span>
            </button>

            {/* the call controls */}
            <div className="flex flex-none items-center gap-0.5">
              <DockButton label={state.muted ? 'Unmute' : 'Mute'} on={state.muted} onClick={() => act('mute')}>
                {state.muted ? <MicOff /> : <Mic />}
              </DockButton>
              <DockButton label={state.deafened ? 'Undeafen' : 'Deafen'} on={state.deafened} onClick={() => act('deafen')}>
                {state.deafened ? <HeadsetOff /> : <Headset />}
              </DockButton>
              <DockButton
                label={state.sharing ? 'Share something else' : 'Share your screen'}
                on={state.sharing}
                onClick={() => act('share')}
              >
                <ScreenIcon />
              </DockButton>
              <DockButton label="Disconnect" danger onClick={() => act('leave')}>
                <Hangup />
              </DockButton>
            </div>

            <span className={cn('mx-1 h-6 w-px flex-none bg-line')} />

            <div className="flex flex-none items-center gap-0.5">
              <DockButton label="Voice settings" onClick={() => act('settings')}>
                <Sliders />
              </DockButton>
              <button
                type="button"
                title="Collapse"
                onClick={() => collapse(true)}
                className="no-drag grid h-[34px] w-[26px] flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] text-mute transition-colors hover:bg-surface-3 hover:text-ink"
              >
                <Icon name="chevronL" size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
